import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Button,
  Row,
  Col,
  Space,
  Typography,
  Alert,
  Card,
  Tag,
  Popover,
  Spin,
  Empty,
} from "antd";
import {
  EnvironmentOutlined,
  HomeOutlined,
  BuildOutlined,
  ApartmentOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  RotateRightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import trans from "../../utils";
import ImageMapViewer from "../snagLocation/ImageMapViewer";
import { InputSelect } from "../Helpers/Input/RefactoredInput";
import {
  useGetPOIZonesByParentIdQuery,
  useGetPOIProjectsQuery,
  useGetPOIBuildingsQuery,
} from "../../Redux/service/POIS/POIs";
import {
  useGetFloorplanQuery,
  useGetSnagReportQuery,
} from "../../Redux/service/Snag/Snag";
import "./SnagReport.css";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text, Paragraph } = Typography;

/**
 * SnagReport Component
 * Flow: Project → Building → Level → Space
 * Shows map with snag data displayed as popovers on pins
 */
const SnagReport = () => {
  const isMobile = useIsMobile();
  const [projectId, setProjectId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [buildingId, setBuildingId] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [mapKey, setMapKey] = useState(0);
  const [selectedPin, setSelectedPin] = useState(null);
  const [snagReportParams, setSnagReportParams] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  // Mobile only: when true, show full-screen map to view report (map not rendered in form)
  const [mobileMapViewOpen, setMobileMapViewOpen] = useState(false);

  // Fetch projects
  const { data: projectsData } = useGetPOIProjectsQuery();

  // Fetch zones based on selected project
  const { data: zonesData, isFetching: zonesLoading } =
    useGetPOIZonesByParentIdQuery(
      { id: projectId },
      {
        skip: !projectId,
      },
    );

  // Fetch buildings based on selected zone
  const { data: buildingsData, isFetching: buildingsLoading } =
    useGetPOIBuildingsQuery(
      { id: zoneId },
      {
        skip: !zoneId,
      },
    );

  // Determine which map to show
  const getCurrentMapId = () => {
    if (buildingId) return buildingId;
    if (zoneId) return zoneId;
    if (projectId) return projectId;
    return null;
  };

  const currentMapId = getCurrentMapId();

  // Fetch current map
  const { data: currentMapData, isFetching: currentMapLoading } =
    useGetFloorplanQuery(currentMapId, {
      skip: !currentMapId,
    });

  // Fetch snag report data
  const { data: snagReportData, isFetching: snagReportLoading } =
    useGetSnagReportQuery(snagReportParams, {
      skip: !snagReportParams,
    });

  // Trigger animation when map changes
  useEffect(() => {
    if (currentMapId) {
      setMapKey((prev) => prev + 1);
    }
  }, [currentMapId]);

  // Reset dependent fields when parent changes
  useEffect(() => {
    if (!projectId) {
      setZoneId("");
      setBuildingId("");
      setSpaceId("");
      setSnagReportParams(null);
      setMobileMapViewOpen(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!zoneId) {
      setBuildingId("");
      setSpaceId("");
      setSnagReportParams(null);
      setMobileMapViewOpen(false);
    }
  }, [zoneId]);

  useEffect(() => {
    if (!buildingId) {
      setSpaceId("");
      setSnagReportParams(null);
      setMobileMapViewOpen(false);
    }
  }, [buildingId]);

  // Prepare options
  const projectsOptions =
    projectsData?.data?.map((project) => ({
      label: project.name,
      value: project.id,
    })) || [];

  const zonesOptions =
    zonesData?.data?.map((zone) => ({
      label: zone.name,
      value: zone.id,
      zone: zone,
    })) || [];

  const buildingsOptions =
    buildingsData?.data?.map((building) => ({
      label: building.name,
      value: building.id,
      building: building,
    })) || [];

  // Get current map data and items based on step
  const getCurrentMapData = () => {
    if (!currentMapData?.data) return null;

    if (buildingId) {
      return {
        mapData: currentMapData,
        loading: currentMapLoading,
        items: currentMapData.data.rooms || [],
        floorPlan: currentMapData.data.floorPlan,
        itemType: "space",
        title: trans("Level Map - View Snags"),
      };
    } else if (zoneId) {
      return {
        mapData: currentMapData,
        loading: currentMapLoading,
        items: currentMapData.data.rooms || [],
        floorPlan: currentMapData.data.floorPlan,
        itemType: "building",
        title: trans("Zone/Building Map - View Snags"),
      };
    } else if (projectId) {
      return {
        mapData: currentMapData,
        loading: currentMapLoading,
        items: currentMapData.data.rooms || [],
        floorPlan: currentMapData.data.floorPlan,
        itemType: "zone",
        title: trans("Project Map - View Snags"),
      };
    }
    return null;
  };

  const currentMap = getCurrentMapData();

  // Prepare dropdown options for space selection
  const getSpaceOptions = () => {
    if (buildingId && currentMap && currentMap.itemType === "space") {
      return (
        currentMap.items?.map((item) => ({
          label: item.displayName || item.code,
          value: item.poiId,
          item: item,
        })) || []
      );
    }
    return [];
  };

  const spaceOptions = getSpaceOptions();

  // Handle item selection from dropdown
  const handleItemSelect = (value, type) => {
    if (type === "project") {
      setProjectId(value);
    } else if (type === "zone") {
      setZoneId(value);
    } else if (type === "building") {
      setBuildingId(value);
    } else if (type === "space") {
      setSpaceId(value);
    }
  };

  const handlePinClick = (item, event) => {
    if (event) {
      event.stopPropagation();
      const mapContainer = event.currentTarget.closest(".map-container-snag");
      if (mapContainer) {
        const rect = mapContainer.getBoundingClientRect();
        setPopoverPosition({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
      }
    }

    setSelectedPin(item);

    let params = {
      projectId: projectId,
      status: "OPEN",
      // startDate: "2025-01-01",
      // endDate: "2025-12-31",
    };

    if (currentMap?.itemType === "zone") {
      // Get snags for this specific zone
      params.zoneId = item.poiId;
      params.poiTypes = "ZONE";
      console.log("🏢 Zone clicked, zoneId:", item.poiId);
    } else if (currentMap?.itemType === "building") {
      // Get snags for this specific level
      params.zoneId = zoneId;
      params.levelId = item.poiId;
      params.poiTypes = "LEVEL";
      console.log("🏗️ Level clicked, levelId:", item.poiId);
    } else if (currentMap?.itemType === "space") {
      // Get snags for this specific room
      params.zoneId = zoneId;
      params.levelId = buildingId;
      params.roomId = item.poiId;
      params.poiTypes = "ROOM";
      console.log("🚪 Room clicked, roomId:", item.poiId);
    }

    // Remove undefined values
    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key],
    );

    // Force new fetch by creating new object with timestamp
    setSnagReportParams({ ...params, _timestamp: Date.now() });
  };

  // Drill down to the next map level from the popover action (project → zone → level)
  const handleViewPinMap = () => {
    if (!selectedPin || !currentMap) return;

    if (currentMap.itemType === "zone") {
      // From project map, go to this building/zone map
      setZoneId(selectedPin.poiId);
    } else if (currentMap.itemType === "building") {
      // From building/zone map, go to this level map
      setBuildingId(selectedPin.poiId);
    }

    // Close current popover
    setSelectedPin(null);
  };

  // Handle double click - select location in dropdown
  const handlePinDoubleClick = (item, event) => {
    if (event) {
      event.stopPropagation();
    }

    // Determine which dropdown to update based on current map type
    if (currentMap?.itemType === "zone") {
      // Double clicked on a zone/building - select it
      const zone = zonesOptions.find(
        (z) =>
          z.zone.code === item.code ||
          z.zone.name === item.name ||
          z.zone.name === item.displayName ||
          item.code === z.value?.toString() ||
          item.name === z.label ||
          z.zone.id === item.poiId,
      );
      if (zone) {
        setZoneId(zone.value);
      }
    } else if (currentMap?.itemType === "building") {
      // Double clicked on a building/level - select it
      const building = buildingsOptions.find(
        (b) =>
          b.building.code === item.code ||
          b.building.name === item.name ||
          b.building.name === item.displayName ||
          item.code === b.value?.toString() ||
          item.name === b.label ||
          b.building.id === item.poiId,
      );
      if (building) {
        setBuildingId(building.value);
      }
    } else if (currentMap?.itemType === "space") {
      // Double clicked on a space - select it
      const space = spaceOptions.find(
        (s) =>
          s.item.poiId === item.poiId ||
          s.item.code === item.code ||
          s.value === item.poiId,
      );
      if (space) {
        setSpaceId(space.value);
      }
    }
  };

  // Get POI data for selected pin
  const getPoiForPin = () => {
    if (!snagReportData?.data?.data?.pois || !selectedPin) return null;

    const pois = snagReportData.data.pois;

    // Find POI that matches the selected pin
    const matchedPoi = pois.find((poi) => {
      // Try multiple matching strategies
      const idMatch =
        poi.id === selectedPin.poiId ||
        poi.id?.toString() === selectedPin.poiId?.toString() ||
        poi.id === parseInt(selectedPin.poiId) ||
        selectedPin.poiId === parseInt(poi.id);

      const codeMatch =
        poi.code === selectedPin.code ||
        (poi.code &&
          selectedPin.code &&
          poi.code.toString() === selectedPin.code.toString());

      const nameMatch =
        poi.name === selectedPin.displayName ||
        poi.name === selectedPin.name ||
        (poi.name &&
          selectedPin.displayName &&
          poi.name.toLowerCase() === selectedPin.displayName.toLowerCase()) ||
        (poi.name &&
          selectedPin.name &&
          poi.name.toLowerCase() === selectedPin.name.toLowerCase());

      return idMatch || codeMatch || nameMatch;
    });

    // If no match found, return the first POI if there's only one (fallback)
    if (!matchedPoi && pois.length === 1) {
      return pois[0];
    }

    return matchedPoi || null;
  };

  const selectedPoi = getPoiForPin();
  const statistics = snagReportData?.data?.statistics;
  // Build image URL
  const getImageUrl = (floorPlan) => {
    if (!floorPlan?.imageUrl) return null;
    return floorPlan.imageUrl;
  };

  const currentImageUrl = currentMap ? getImageUrl(currentMap.floorPlan) : null;

  // Popover content for snag data
  const renderSnagPopover = () => {
    if (snagReportLoading) {
      return (
        <div style={{ padding: "16px", textAlign: "center" }}>
          <Spin size="small" />
          <div style={{ marginTop: 8 }}>{trans("Loading snags...")}</div>
        </div>
      );
    }

    if (!snagReportData?.data) {
      return (
        <div
          style={{ padding: "16px", textAlign: "center", minWidth: "200px" }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={trans("No data available")}
          />
        </div>
      );
    }

    const poiData = selectedPoi;
    // If no POI match found, try to use the first POI from the response
    const fallbackPoi =
      !poiData && snagReportData?.data?.pois?.length > 0
        ? snagReportData.data.pois[0]
        : null;
    const finalPoiData = poiData || fallbackPoi;
    const poiStats = finalPoiData?.snagStats;
    const overview = statistics?.overview;
    const statusBreakdown =
      poiStats?.statusBreakdown || statistics?.statusBreakdown;
    const canDrillDownToMap =
      currentMap &&
      currentMap.itemType &&
      currentMap.itemType !== "space" &&
      !!selectedPin;

    // Format closure rate as percentage
    const formatClosureRate = (rate) => {
      if (rate === null || rate === undefined) return "0%";
      if (typeof rate === "number") return `${rate}%`;
      return rate;
    };

    return (
      <div
        style={
          isMobile
            ? undefined
            : { maxWidth: "450px", maxHeight: "600px", overflowY: "auto" }
        }
      >
        {/* POI Information */}
        {finalPoiData && (
          <div
            style={{
              marginBottom: "16px",
              paddingBottom: "12px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div>
                <Text strong style={{ fontSize: "16px" }}>
                  {finalPoiData.name || finalPoiData.code || trans("Location")}
                </Text>
                {finalPoiData.type && (
                  <Tag color="blue" style={{ marginLeft: "8px" }}>
                    {finalPoiData.type.toUpperCase()}
                  </Tag>
                )}
              </div>
              {canDrillDownToMap && (
                <Button
                  type="link"
                  size="small"
                  icon={<EnvironmentOutlined />}
                  onClick={handleViewPinMap}
                  style={{ paddingRight: 0 }}
                >
                  {trans("View this location on map")}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* POI Snag Statistics - Always show if available */}
        {(poiStats || (overview && !finalPoiData)) && (
          <Card size="small" style={{ marginBottom: "12px" }}>
            <Text strong style={{ marginBottom: "8px", display: "block" }}>
              {trans("Snag Statistics")}
            </Text>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong>{trans("Total Snags")}</Text>
                <Text strong style={{ fontSize: "18px", color: "#1890ff" }}>
                  {poiStats?.totalSnags || overview?.totalSnags || 0}
                </Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{trans("Open")}</Text>
                <Tag color="red">
                  {poiStats?.openSnags || overview?.openSnags || 0}
                </Tag>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{trans("Closed")}</Text>
                <Tag color="green">
                  {poiStats?.closedSnags || overview?.closedSnags || 0}
                </Tag>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{trans("Closure Rate")}</Text>
                <Text strong>
                  {formatClosureRate(
                    poiStats?.closureRate || overview?.closureRate,
                  )}
                </Text>
              </div>
            </Space>
          </Card>
        )}

        {/* Status Breakdown */}
        {statusBreakdown && (
          <Card size="small" style={{ marginBottom: "12px" }}>
            <Text strong style={{ marginBottom: "8px", display: "block" }}>
              {trans("Status Breakdown")}
            </Text>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {Object.entries(statusBreakdown).map(([status, count]) => {
                if (count === 0) return null;
                const colorMap = {
                  OPEN: "red",
                  CLOSED: "green",
                  FIX: "orange",
                  RECTIFY: "blue",
                  VERIFY: "cyan",
                  REOPEN: "purple",
                  IN_DISPUTE: "volcano",
                };
                return (
                  <div
                    key={status}
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Tag color={colorMap[status] || "default"}>{status}</Tag>
                    <Text strong>{count}</Text>
                  </div>
                );
              })}
            </Space>
          </Card>
        )}

        {/* Overview Statistics (if no specific POI selected) */}
        {!finalPoiData && overview && (
          <Card size="small">
            <Text strong style={{ marginBottom: "8px", display: "block" }}>
              {trans("Overview")}
            </Text>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{trans("Total POIs with Snags")}</Text>
                <Text strong>{overview.totalPoisWithSnags}</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{trans("Total Snags")}</Text>
                <Text strong>{overview.totalSnags}</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{trans("Open Snags")}</Text>
                <Tag color="red">{overview.openSnags}</Tag>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{trans("Closed Snags")}</Text>
                <Tag color="green">{overview.closedSnags}</Tag>
              </div>
            </Space>
          </Card>
        )}

        {/* Last Snag Reported */}
        {finalPoiData?.lastSnagReported && (
          <div
            style={{ marginTop: "12px", fontSize: "12px", color: "#8c8c8c" }}
          >
            <CalendarOutlined /> {trans("Last reported")}:{" "}
            {new Date(finalPoiData.lastSnagReported).toLocaleDateString()}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="snag-report-page">
        {!isMobile && (
          <div className="snag-report-header">
            <Title level={2}>{trans("Snag Report")}</Title>
            <Paragraph type="secondary">
              {trans("Select location filters to view snags on the map")}
            </Paragraph>
          </div>
        )}
        {/* Help Banner */}
        {!isMobile && (
          <Alert
            message={trans("How it works")}
            description={trans(
              "Select each level step by step: Project → Building → Level → Space. Click on pins in the map to view snag details.",
            )}
            type="info"
            showIcon
            closable
            style={{ marginBottom: 20 }}
          />
        )}

        {/* Selection Dropdowns */}
        <Card className="selection-card" bordered={false}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
              <div className="input-with-icon">
                <HomeOutlined className="input-icon" />
                <InputSelect
                  id="report-project"
                  label={trans("Project")}
                  placeholder={trans("Choose project")}
                  options={projectsOptions}
                  value={projectId}
                  onChange={(val) => handleItemSelect(val, "project")}
                  formstyle="form-input"
                  showSearch
                />
              </div>
              {!projectId && (
                <div className="field-hint">
                  <InfoCircleOutlined /> {trans("Start here")}
                </div>
              )}
            </Col>
            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
              <div className="input-with-icon">
                <BuildOutlined className="input-icon" />
                <InputSelect
                  id="report-zone"
                  label={trans("Building")}
                  placeholder={
                    projectId
                      ? trans("Choose building")
                      : trans("Select project first")
                  }
                  options={zonesOptions}
                  value={zoneId || ""}
                  onChange={(val) => handleItemSelect(val, "zone")}
                  formstyle="form-input"
                  disabled={!projectId}
                  loading={zonesLoading}
                  showSearch
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
              <div className="input-with-icon">
                <ApartmentOutlined className="input-icon" />
                <InputSelect
                  id="report-building"
                  label={trans("Level")}
                  placeholder={
                    zoneId
                      ? trans("Choose level")
                      : trans("Select building first")
                  }
                  options={buildingsOptions}
                  value={buildingId || ""}
                  onChange={(val) => handleItemSelect(val, "building")}
                  formstyle="form-input"
                  disabled={!zoneId}
                  loading={buildingsLoading}
                  showSearch
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
              <div className="input-with-icon">
                <EnvironmentOutlined className="input-icon" />
                <InputSelect
                  id="report-space"
                  label={trans("Space")}
                  placeholder={
                    buildingId
                      ? trans("Choose space (optional)")
                      : trans("Select level first")
                  }
                  options={spaceOptions}
                  value={spaceId || ""}
                  onChange={(val) => handleItemSelect(val, "space")}
                  formstyle="form-input"
                  disabled={!buildingId}
                  showSearch
                  allowClear
                />
              </div>
            </Col>
          </Row>
        </Card>

        {/* Map Section */}
        {!currentMap && projectId && (
          <div className="empty-map-state">
            <EnvironmentOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />
            <Paragraph type="secondary" style={{ marginTop: 16 }}>
              {trans(
                "Complete the selections above to view the interactive map",
              )}
            </Paragraph>
          </div>
        )}

        {/* Desktop: inline map; Mobile: use 'Show report' button to open full-screen map */}
        {currentMap && !isMobile && (
          <div className="map-section" key={mapKey}>
            <div className="map-header">
              <div className="map-title-row">
                <Tag color="blue" icon={<EnvironmentOutlined />}>
                  {currentMap.itemType === "zone" &&
                    trans("Building Selection")}
                  {currentMap.itemType === "building" &&
                    trans("Level Selection")}
                  {currentMap.itemType === "space" && trans("Space Selection")}
                </Tag>
                <Text strong>{currentMap.title}</Text>
              </div>
            </div>

            <div
              className="map-container-snag"
              style={{ position: "relative" }}
            >
              <ImageMapViewer
                imageUrl={currentImageUrl}
                imageWidth={currentMap?.floorPlan?.width}
                imageHeight={currentMap?.floorPlan?.height}
                rooms={currentMap?.items || []}
                selectedRoomCode={selectedPin?.code}
                onRoomClick={handlePinClick}
                onRoomDoubleClick={handlePinDoubleClick}
                loading={currentMap?.loading}
              />
              {selectedPin && (
                <div
                  style={{
                    position: "absolute",
                    left: `${popoverPosition.x}px`,
                    top: `${popoverPosition.y}px`,
                    zIndex: 1000,
                    transform: "translate(-50%, -100%)",
                    marginTop: "10px",
                  }}
                >
                  <Popover
                    content={renderSnagPopover()}
                    title={`${trans("Snags at")} ${
                      selectedPin?.displayName || selectedPin?.code
                    }`}
                    trigger="click"
                    open={selectedPin !== null}
                    onOpenChange={(open) => {
                      if (!open) setSelectedPin(null);
                    }}
                    placement="right"
                    getPopupContainer={() => document.body}
                  >
                    <div style={{ width: "1px", height: "1px" }} />
                  </Popover>
                </div>
              )}
            </div>
          </div>
        )}

        {isMobile && currentMap && !mobileMapViewOpen && (
          <div className="snag-report-mobile-open-map-section">
            <Button
              type="primary"
              size="large"
              icon={<EnvironmentOutlined />}
              onClick={() => setMobileMapViewOpen(true)}
              block
              className="snag-report-mobile-open-map-btn"
            >
              {trans("Show report on map")}
            </Button>
          </div>
        )}
      </div>

      {/* Mobile only: full-screen map overlay to show report, user can go back to change filters */}
      {isMobile &&
        mobileMapViewOpen &&
        currentMap &&
        ReactDOM.createPortal(
          <div className="snag-location-mobile-map-overlay">
            <header className="snag-location-mobile-map-header">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => setMobileMapViewOpen(false)}
                className="snag-location-mobile-map-back"
              />
              <span className="snag-location-mobile-map-title">
                {trans("Snag report map")}
              </span>
            </header>
            <div className="snag-location-mobile-map-landscape-hint">
              <RotateRightOutlined />{" "}
              {trans("For best experience, use landscape orientation")}
            </div>
            <div className="snag-location-mobile-map-body">
              <div className="snag-location-mobile-map-viewer">
                <ImageMapViewer
                  key={mapKey}
                  imageUrl={currentImageUrl}
                  imageWidth={currentMap?.floorPlan?.width}
                  imageHeight={currentMap?.floorPlan?.height}
                  rooms={currentMap?.items || []}
                  selectedRoomCode={selectedPin?.code}
                  onRoomClick={handlePinClick}
                  onRoomDoubleClick={handlePinDoubleClick}
                  loading={currentMap?.loading}
                  isMobile={true}
                />
                {selectedPin && (
                  <div className="snag-report-mobile-fullpage-popover">
                    <div className="snag-report-mobile-fullpage-popover-header">
                      <span className="snag-report-mobile-fullpage-popover-title">
                        {`${trans("Snags at")} ${selectedPin?.displayName || selectedPin?.code}`}
                      </span>
                      <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() => setSelectedPin(null)}
                        className="snag-report-mobile-fullpage-popover-close"
                      />
                    </div>
                    <div className="snag-report-mobile-fullpage-popover-body">
                      {renderSnagPopover()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SnagReport;
